'use client'

/**
 * Realtime 示範用的 PCM 串流 hook:getUserMedia → AudioContext → AudioWorklet,
 * 把麥克風音訊以 ~100ms 的 base64 pcm_s16le mono frame 透過 `onFrame` 推給呼叫端
 * (即 `PcmSourceAdapter` 的 frame contract,見 packages/voice/src/pcm-source.ts)。
 * 同時掛一個 AnalyserNode 供波形視覺 (`getLevels`)。
 *
 * 與 `use-mic-recorder.ts`(批次 blob 錄音,fallback 用)職責分離:那支錄整段
 * 音檔、這支串 frame。所有瀏覽器 API 都在 client 端 guard,SSR 安全;sampleRate
 * 直接用 `AudioContext.sampleRate`(不重新取樣,relay 會原樣轉給上游 STT)。
 */

import { reportHandledException } from '@/lib/error-reporting'
import { useCallback, useEffect, useRef } from 'react'
import { downsampleFrequencyData } from './analyser-levels'
import { DEMO_MIC_CONSTRAINTS } from './mic-capture'
import { raceWithTimeout } from './race-with-timeout'

/** worklet 以靜態資產載入;集中成常數方便切換 URL。 */
const PCM_WORKLET_URL = '/pcm-frame-processor.js'

/** prepareContext 於「已取消/逾時」時拋出的 sentinel:呼叫端據此走取消收尾(關 context)。 */
const START_CANCELLED = Symbol('pcm-start-cancelled')

export type PcmStartResult =
  | { ok: true; sampleRate: number }
  | { ok: false; reason: 'unsupported' | 'permission_denied' }

export interface PcmStreamControls {
  /** AudioContext + getUserMedia + AudioWorklet 是否都可用。 */
  isSupported: () => boolean
  /**
   * 預熱 PCM pipeline:在真正 start() 前先建好 AudioContext 並載入 worklet module,
   * 讓「按下即錄音」時省掉 AudioContext 建立 + addModule 的 ~50–150ms(那段時間
   * realtime STT 只收得到 worklet frame,會吃掉語句開頭)。冪等:已有 warm cache
   * 或正在擷取中都是 no-op。在使用者手勢內(pointerdown)呼叫,對 iOS Safari 的
   * AudioContext 建立才合法。載入失敗只 warn 並丟掉 cache — start() 會走 cold path,
   * 絕不因壞掉的 warm cache 讓 start() 連帶失敗。
   */
  warmup: () => Promise<void>
  /**
   * 啟動擷取;ok 時回傳 AudioContext 實際取樣率(原樣送往 relay)。
   * 傳入共享 `MediaStream` 時直接沿用(不再 getUserMedia),與 blob recorder 共用
   * 單一麥克風擷取 — 對同一支實體麥克風開兩路擷取會在真實硬體上互搶、其中一路
   * 收到損毀/靜音音訊。共享 stream 時本 hook 不會 stop 它的 tracks(擁有者負責)。
   * 若已 warmup() 過(warm cache 存在)則沿用該 context 並跳過 addModule。
   *
   * epoch/abort 機制:每次 start() 記下進入時的 epoch;`stop()` 與 unmount 會 bump epoch,
   * 於是任何 await 邊界後只要 epoch 已變(或已 unmount)就自我作廢 —— 關掉手上的 context、
   * **絕不 assign sessionRef**。因此「同 hook 兩個並發 start()」不可能雙活(舊的必然 stale)。
   * `opts.timeoutMs` 存在時把「module 載入 + resume」包進逾時 race:逾時 → 關 context、回
   * {ok:false}(遲到的 moduleReady 無害,context 已關不建 graph)。
   */
  start: (externalStream?: MediaStream, opts?: { timeoutMs?: number }) => Promise<PcmStartResult>
  /** 拆除 worklet node + 停止 tracks + 關閉 AudioContext。 */
  stop: () => Promise<void>
  /** 訂閱 base64 pcm_s16le mono frame;回傳取消訂閱函式。 */
  onFrame: (callback: (base64PcmFrame: string) => void) => () => void
  /** 把目前頻譜降採樣成 count 個 0..1 音量值(無 analyser 時全 0)。 */
  getLevels: (count: number) => number[]
}

interface ActiveSession {
  stream: MediaStream
  /** True when this hook opened the stream (so it must stop the tracks). False
   *  for a caller-owned shared stream — stopping it would kill the blob recorder. */
  ownsStream: boolean
  audioContext: AudioContext
  workletNode: AudioWorkletNode
  /** 靜音 sink，保活 graph 讓 worklet 的 process() 持續執行（見 start 註解）。 */
  sink: GainNode
  analyser: AnalyserNode | null
  freqData: Uint8Array | null
}

function resolveAudioContextCtor(): typeof AudioContext | undefined {
  if (typeof AudioContext !== 'undefined') return AudioContext
  if (typeof window === 'undefined') return undefined
  return (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
}

/** 關閉 AudioContext,失敗只記錄不擲回(關閉視覺/擷取資源失敗不影響流程)。冪等。 */
async function closeContextQuietly(audioContext: AudioContext, label: string): Promise<void> {
  if (audioContext.state === 'closed') return
  try {
    await audioContext.close()
  } catch (error: unknown) {
    reportHandledException(error, { source: 'lib/use-pcm-stream.ts' })
    console.warn(`[landing-demo] failed to close ${label}`, error)
  }
}

/** warmup() 預建的 AudioContext + 進行中的 addModule,供接下來的 start() 沿用。 */
interface WarmCache {
  audioContext: AudioContext
  moduleReady: Promise<void>
}

export function usePcmStream(): PcmStreamControls {
  const sessionRef = useRef<ActiveSession | null>(null)
  const listenersRef = useRef(new Set<(frame: string) => void>())
  const warmCacheRef = useRef<WarmCache | null>(null)
  // 元件是否已卸載:start() 在 getUserMedia / moduleReady / addModule 等 await 邊界之後,
  // 若已卸載則關掉手上的 context、不建 graph、不 assign session(否則 AudioContext 洩漏)。
  const unmountedRef = useRef(false)
  // start() 的世代序號:stop() 與 unmount 都 bump 它。in-flight 的 start() 於各 await 邊界
  // 比對進入時的 epoch,一旦變動就自我作廢(不建 graph、不 assign session)。這讓「卡住的
  // start 之後 resolve」不會漏回收、不會建第二條 worklet 餵同一 listenersRef、不會 assign
  // stale sessionRef 讓下一 take 拿到掛在舊 stream 的 session。
  const epochRef = useRef(0)

  const isSupported = useCallback(() => {
    return (
      typeof navigator !== 'undefined' &&
      typeof navigator.mediaDevices?.getUserMedia === 'function' &&
      resolveAudioContextCtor() !== undefined &&
      typeof AudioWorkletNode !== 'undefined'
    )
  }, [])

  const releaseSession = useCallback(async () => {
    const session = sessionRef.current
    if (!session) return
    sessionRef.current = null

    // 請 worklet flush 殘餘 buffer 後立刻解綁 onmessage —— 已知限制:onmessage 於此同步
    // 清空,flush 出的 <100ms 尾巴 frame 一律丟棄(best-effort,不遞送給訂閱者)。保留
    // postMessage 是為了讓 worklet 端有機會停止累積,不是為了送尾巴。
    session.workletNode.port.postMessage({ type: 'flush' })
    session.workletNode.port.onmessage = null
    session.workletNode.disconnect()
    session.sink.disconnect()
    // Only stop tracks we opened; a shared (caller-owned) stream is stopped by its
    // owner — stopping it here would cut off the blob recorder sharing it.
    if (session.ownsStream) {
      for (const track of session.stream.getTracks()) track.stop()
    }
    await closeContextQuietly(session.audioContext, 'PCM AudioContext')
  }, [])

  /** 丟掉 warm cache:關掉預建但未被 start() 消費的 AudioContext(免洩漏)。冪等。 */
  const discardWarmCache = useCallback(async () => {
    const cache = warmCacheRef.current
    if (!cache) return
    warmCacheRef.current = null
    await closeContextQuietly(cache.audioContext, 'warmed PCM AudioContext')
  }, [])

  const warmup = useCallback(async (): Promise<void> => {
    // 環境不支援、已卸載、擷取進行中、或已有 warm cache → no-op(冪等)。
    if (!isSupported() || unmountedRef.current || sessionRef.current || warmCacheRef.current) return

    const AudioContextCtor = resolveAudioContextCtor()!
    // 建 context → 存 cache 全納入 try:任何同步 throw(建構失敗 / audioWorklet 缺 /
    // addModule 同步擲回)都要關掉已建但未進 cache 的 context,免孤兒。
    let audioContext: AudioContext | undefined
    try {
      audioContext = new AudioContextCtor()
      if (!audioContext.audioWorklet) {
        // 舊瀏覽器無 audioWorklet → 無法預熱;關掉 context(start() 自行判定 unsupported)。
        await closeContextQuietly(audioContext, 'warmed PCM AudioContext')
        return
      }
      // 先啟動 addModule 並同步存進 cache(讓並行的第二次 warmup 立即 no-op)。
      const moduleReady = audioContext.audioWorklet.addModule(PCM_WORKLET_URL)
      warmCacheRef.current = { audioContext, moduleReady }
      // 手勢內(pointerdown)發起 resume:start() 的 resume 已隔多個 await 邊界,iOS Safari
      // 嚴格 autoplay 可能不理;在此先啟動較穩(失敗只 warn,不影響 module 載入)。
      if (audioContext.state === 'suspended') {
        void audioContext.resume().catch((error) => {
          reportHandledException(error, { source: 'lib/use-pcm-stream.ts' })
          console.warn('[landing-demo] failed to resume warmed PCM AudioContext', error)
        })
      }
      await moduleReady
    } catch (error) {
      reportHandledException(error, { source: 'lib/use-pcm-stream.ts' })
      // addModule 失敗 / 同步 throw → 丟掉 warm cache(關 context),start() 改走 cold path。
      console.warn(
        '[landing-demo] PCM warmup failed — discarding warm cache, start() will cold-start',
        error
      )
      warmCacheRef.current = null
      if (audioContext) await closeContextQuietly(audioContext, 'warmed PCM AudioContext')
    }
  }, [isSupported])

  const start = useCallback(
    async (
      externalStream?: MediaStream,
      opts?: { timeoutMs?: number }
    ): Promise<PcmStartResult> => {
      if (!isSupported()) return { ok: false, reason: 'unsupported' }
      if (sessionRef.current) {
        return { ok: true, sampleRate: sessionRef.current.audioContext.sampleRate }
      }
      // S7:entry 自 bump epoch —— 任何後起的 start() 自動使前一個 in-flight start 失效
      // (hook 層面保證,不再依賴呼叫端);stop() / unmount 也 bump epoch。
      epochRef.current += 1
      const epoch = epochRef.current
      // 逾時勝出時由下方設 true;與 epoch/unmounted 併為統一的「取消」判斷(S1)。
      let timedOut = false
      const isCancelled = () => epochRef.current !== epoch || unmountedRef.current || timedOut

      // A caller-supplied shared stream skips getUserMedia (single mic capture, no
      // race with the blob recorder). Otherwise open our own (owned → we stop it).
      let stream: MediaStream
      const ownsStream = externalStream === undefined
      if (externalStream) {
        stream = externalStream
      } else {
        try {
          // 同 demo card 的共享擷取:關 EC/NS/AGC(見 DEMO_MIC_CONSTRAINTS)。
          stream = (await navigator.mediaDevices.getUserMedia(DEMO_MIC_CONSTRAINTS)) as MediaStream
        } catch (error) {
          reportHandledException(error, { source: 'lib/use-pcm-stream.ts' })
          // 使用者拒絕或裝置不可用 — 呼叫端會退回批次路徑(可見的 recovery)
          console.warn('[landing-demo] microphone unavailable for PCM stream', error)
          return { ok: false, reason: 'permission_denied' }
        }
      }
      const stopOwnedStream = () => {
        if (ownsStream) for (const track of stream.getTracks()) track.stop()
      }
      // getUserMedia await 後:已被作廢(stop()/unmount bump epoch)→ 停自有 stream、早退。
      if (isCancelled()) {
        stopOwnedStream()
        return { ok: false, reason: 'unsupported' }
      }

      // 消費 warmup() 預建的 context(module 進行中);否則 cold path 新建 context。
      const warm = warmCacheRef.current
      warmCacheRef.current = null
      let audioContext: AudioContext = warm ? warm.audioContext : new (resolveAudioContextCtor()!)()
      let moduleLoaded = false

      // 「載入 module + resume」可能因 addModule 網路卡住 / resume 懸置而永不 settle。
      // warm cache 壞掉(moduleReady reject)就關掉它、cold-restart 新建 context 再載一次 ——
      // 但 S1:若此刻已取消/逾時(isCancelled),**不冷重啟**(那顆新 context 只活在 closure、
      // start 已 return → 永久洩漏),改為關掉手上的 context、拋 START_CANCELLED。
      const prepareContext = async (): Promise<void> => {
        if (warm) {
          try {
            await warm.moduleReady
            moduleLoaded = true
          } catch (error) {
            if (isCancelled()) {
              await closeContextQuietly(audioContext, 'warmed PCM AudioContext (cancelled)')
              throw START_CANCELLED
            }
            reportHandledException(error, { source: 'lib/use-pcm-stream.ts' })
            console.warn('[landing-demo] warmed PCM cache unusable — cold-starting', error)
            await closeContextQuietly(audioContext, 'warmed PCM AudioContext')
            audioContext = new (resolveAudioContextCtor()!)()
          }
        }
        if (audioContext.state === 'suspended') await audioContext.resume()
        if (!moduleLoaded) {
          await audioContext.audioWorklet.addModule(PCM_WORKLET_URL)
          moduleLoaded = true
        }
      }

      try {
        // timeoutMs 存在時把 prepareContext 包進逾時 race(共用 raceWithTimeout;'timeout'
        // sentinel 用 fallback 值表達)。逾時勝出 → 設 timedOut(讓 prepareContext 的遲到路徑
        // isCancelled → 不冷重啟)+ 關 context;prepareContext 之後才 reject(context 已關)只 warn。
        if (opts?.timeoutMs !== undefined) {
          const prep = prepareContext()
          const outcome = await raceWithTimeout(
            prep.then(() => 'ok' as const),
            opts.timeoutMs,
            'timeout' as const
          )
          if (outcome === 'timeout') {
            timedOut = true
            void prep.catch((error) => {
              reportHandledException(error, { source: 'lib/use-pcm-stream.ts' })
              if (error === START_CANCELLED) return
              console.warn('[landing-demo] late PCM prepare failed after timeout', error)
            })
            console.warn(
              '[landing-demo] PCM start timed out — closing context, falling back to batch'
            )
            await closeContextQuietly(audioContext, 'PCM AudioContext (start timeout)')
            stopOwnedStream()
            return { ok: false, reason: 'unsupported' }
          }
        } else {
          await prepareContext()
        }

        // module 載入 / resume await 之後:已被作廢(epoch/unmount)→ 關 context、停自有 stream、絕不 assign。
        if (isCancelled()) {
          await closeContextQuietly(audioContext, 'PCM AudioContext (cancelled mid-start)')
          stopOwnedStream()
          return { ok: false, reason: 'unsupported' }
        }

        const source = audioContext.createMediaStreamSource(stream)
        const workletNode = new AudioWorkletNode(audioContext, 'pcm-frame-processor')
        workletNode.port.onmessage = (event: MessageEvent<string>) => {
          for (const listener of listenersRef.current) listener(event.data)
        }
        source.connect(workletNode)

        // 關鍵:worklet 只讀 input、不寫 output。若它沒有通往 destination 的路徑,
        // 瀏覽器(Chrome)會把它從 render graph 剪掉 → process() 永遠不執行 →
        // 一個 frame 都送不出去 → 上游 STT 回 "400 No audio received"。接一個
        // gain=0 的靜音 sink 到 destination 保活整條 graph(gain=0 無回授;
        // worklet 本來也不輸出聲音,所以是完全靜音的)。
        const sink = audioContext.createGain()
        sink.gain.value = 0
        workletNode.connect(sink)
        sink.connect(audioContext.destination)

        // analyser 只供波形視覺;失敗不影響 frame 串流
        let analyser: AnalyserNode | null = null
        let freqData: Uint8Array | null = null
        try {
          analyser = audioContext.createAnalyser()
          analyser.fftSize = 256
          source.connect(analyser)
          freqData = new Uint8Array(analyser.frequencyBinCount)
        } catch (error) {
          reportHandledException(error, { source: 'lib/use-pcm-stream.ts' })
          console.warn('[landing-demo] PCM analyser unavailable — wave falls back to static', error)
          analyser = null
          freqData = null
        }

        sessionRef.current = {
          stream,
          ownsStream,
          audioContext,
          workletNode,
          sink,
          analyser,
          freqData,
        }
        return { ok: true, sampleRate: audioContext.sampleRate }
      } catch (error) {
        reportHandledException(error, { source: 'lib/use-pcm-stream.ts' })
        if (error === START_CANCELLED) {
          // prepareContext 取消路徑:已關掉手上的 context;停自有 stream、回 unsupported、不再冷重啟。
          stopOwnedStream()
          return { ok: false, reason: 'unsupported' }
        }
        // worklet 載入或圖建構失敗 — 清理後讓呼叫端退回批次路徑
        console.warn('[landing-demo] failed to start PCM worklet — falling back to batch', error)
        stopOwnedStream()
        await closeContextQuietly(audioContext, 'PCM AudioContext')
        return { ok: false, reason: 'unsupported' }
      }
    },
    [isSupported]
  )

  const stop = useCallback(async (): Promise<void> => {
    // bump epoch:任何 in-flight 的 start() 於下個 await 邊界自我作廢(不建 graph、不 assign)。
    epochRef.current += 1
    await releaseSession()
    // 關掉一個未被 start() 消費的 warm cache(免 AudioContext 洩漏)。
    await discardWarmCache()
  }, [releaseSession, discardWarmCache])

  const onFrame = useCallback((callback: (base64PcmFrame: string) => void): (() => void) => {
    listenersRef.current.add(callback)
    return () => {
      listenersRef.current.delete(callback)
    }
  }, [])

  const getLevels = useCallback((count: number): number[] => {
    const session = sessionRef.current
    if (!session?.analyser || !session.freqData) return new Array<number>(count).fill(0)
    return downsampleFrequencyData(session.analyser, session.freqData, count)
  }, [])

  // unmount:標記已卸載(讓 in-flight 的 start() 於 await 後關 context、不建 session),
  // 並停止擷取、釋放資源(含未被消費的 warm cache)。
  useEffect(() => {
    // remount-safe(React StrictMode dev 雙掛載):setup 時重置,免得前一輪 cleanup 的
    // true 卡住同一個 ref、讓 start()/warmup() 永遠拒絕。
    unmountedRef.current = false
    return () => {
      unmountedRef.current = true
      epochRef.current += 1
      void releaseSession()
      void discardWarmCache()
    }
  }, [releaseSession, discardWarmCache])

  return { isSupported, warmup, start, stop, onFrame, getLevels }
}
